import { FCC } from "@/types/common";
import { Typography, Stack, styled, Link as MuiLink } from "@mui/material";
import { Link } from "react-router-dom";
import { TextWithLinks } from "@/components/commons/TextWithLinks";
import { EMPTY_STR } from "@/constants/string";
import Divider from "@mui/material/Divider";
import { black, red, state, text } from "@/theme/colors";
import { convertNumberToZipCode } from "@/utils/input";
import { FileItem } from "@/components/elements/FileItem";
import { useFileInfo } from "@/api/services/global/file";

type TJobInfoItem = {
  label?: string;
  value?: string | null | undefined;
  direction?: "row" | "column";
  type?: string;
  color?: string;
  suffix?: string;
  required?: boolean;
  endIcon?: React.ReactNode;
  isShowDivider?: boolean;
  internalLinkTo?: string;
};
export const JobInfoItem: FCC<TJobInfoItem> = ({
  label,
  value,
  direction,
  type = "string",
  color,
  suffix,
  endIcon,
  required = false,
  isShowDivider = true,
  internalLinkTo,
}) => {
  const getValue = () => {
    if (!value) return EMPTY_STR.TEXT;
    if (type === "zipcode") {
      return convertNumberToZipCode(value);
    }
    return value;
  };

  const RenderValue: FCC = ({ children }) => {
    return (
      <Typography
        variant="body14"
        color={color ? color : text.primary}
        sx={{
          fontFamily: "inherit",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {children}
      </Typography>
    );
  };

  const getExternalLink = (link: string) => {
    let externalLink = String(link);
    if (!/^https?:\/\//i.test(externalLink)) {
      externalLink = `https://${externalLink}`;
    }
    return externalLink;
  };

  const RenderComponent: FCC = ({}) => {
    if (type === "internalLink" && internalLinkTo) {
      return (
        <RenderValue>
          {value ? (
            <MuiLink
              component={Link}
              to={internalLinkTo}
              color={state.link_1}
              target="_blank"
              sx={{ "&:hover": { color: state.link_1 } }}
            >
              {getValue()}
            </MuiLink>
          ) : (
            getValue()
          )}
        </RenderValue>
      );
    }
    if (type === "link") {
      return (
        <RenderValue>
          {value ? (
            <MuiLink href={getExternalLink(value)} target="_blank" underline="none" color={color} rel="noopener">
              {getValue()}
            </MuiLink>
          ) : (
            getValue()
          )}
        </RenderValue>
      );
    }
    if (type === "textArea") {
      if (!value) return EMPTY_STR.TEXT;

      return (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 14,
            fontFamily: "inherit",
          }}
        >
          <TextWithLinks text={value} />
        </pre>
      );
    }
    if (type === "file") {
      const { data: infoResult, isLoading } = useFileInfo(value ?? "");

      if (isLoading) return null;

      const file = {
        filePath: value ?? "",
        name: infoResult?.data?.name,
      };

      return (
        <RenderValue>
          {value ? (
            <FileItem filePath={value} downloadable iconDownloadable={true} previewEnabled files={file ? [file] : []} hideIcon={true} />
          ) : (
            getValue()
          )}
        </RenderValue>
      );
    }
    return (
      <RenderValue>
        {getValue()}
        {suffix && value ? suffix : ""}
      </RenderValue>
    );
  };

  return (
    <>
      <JobInfoItemWrapper direction={direction} gap={1}>
        {label && (
          <Typography variant="cap12Light" color={black[70]}>
            {label}
            {required && (
              <Typography component="span" variant="cap12" sx={{ color: red[700], ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
        )}
        <Stack direction="row" justifyContent="space-between">
          <RenderComponent />
          <Stack>{endIcon}</Stack>
        </Stack>
        {isShowDivider && <DividerWrapper />}
      </JobInfoItemWrapper>
    </>
  );
};

const JobInfoItemWrapper = styled(Stack)<{ direction?: string }>`
  ${({ direction }) => (direction === "row" ? `align-items: center;` : ``)}
`;

const DividerWrapper = styled(Divider)`
  margin-top: 8px;
  width: 100%;
`;
